import { useState, useEffect, useRef } from "react";
import { http } from "@/lib/api";
import { Send, CheckCircle, Sparkles, MapPin, Phone, MessageSquare, Image } from "lucide-react";
import { toast } from "sonner";

const C = {
  bg: "#070707", bg2: "#11100E", text: "#F5F2EB", text2: "#B5B0A7",
  gold: "#C9A055", purple: "#7C3AED",
};
const F = { cond: "'Saira Condensed', sans-serif", sans: "'DM Sans', 'Outfit', sans-serif", brush: "'Permanent Marker', cursive" };

/* ─── Confetti Particle Component ─── */
function Confetti() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const colors = ["#C9A055", "#E8C96A", "#FF6B6B", "#4ade80", "#7C3AED", "#60A5FA", "#F472B6", "#FBBF24"];
    const particles = Array.from({ length: 120 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height - canvas.height,
      w: Math.random() * 10 + 4,
      h: Math.random() * 6 + 2,
      color: colors[Math.floor(Math.random() * colors.length)],
      speedY: Math.random() * 3 + 1.5,
      speedX: (Math.random() - 0.5) * 2,
      rotation: Math.random() * 360,
      rotSpeed: (Math.random() - 0.5) * 8,
      opacity: 1,
    }));

    let animId;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      let alive = false;
      for (const p of particles) {
        if (p.y > canvas.height + 50) continue;
        alive = true;
        p.y += p.speedY;
        p.x += p.speedX;
        p.rotation += p.rotSpeed;
        if (p.y > canvas.height * 0.7) p.opacity = Math.max(0, p.opacity - 0.008);

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.globalAlpha = p.opacity;
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      }
      if (alive) animId = requestAnimationFrame(animate);
    };
    animate();

    const handleResize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    window.addEventListener("resize", handleResize);
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", handleResize); };
  }, []);

  return <canvas ref={canvasRef} className="fixed inset-0 pointer-events-none z-50" />;
}

/* ─── Animated Sparkle Text ─── */
function SparkleText({ children }) {
  return (
    <span className="relative inline-block">
      <span className="relative z-10">{children}</span>
      <span className="absolute -top-3 -left-3 animate-ping">
        <Sparkles size={18} style={{ color: C.gold }} />
      </span>
      <span className="absolute -bottom-2 -right-4 animate-ping" style={{ animationDelay: "0.4s" }}>
        <Sparkles size={14} style={{ color: "#7C3AED" }} />
      </span>
      <span className="absolute top-1 -right-6 animate-ping" style={{ animationDelay: "0.8s" }}>
        <Sparkles size={12} style={{ color: "#4ade80" }} />
      </span>
    </span>
  );
}

export default function NameWinner() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    artwork_name: "",
    phone: "",
    address: "",
    message: "",
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Please enter your name"); return; }
    if (!form.artwork_name.trim()) { toast.error("Please enter the artwork name you chose"); return; }
    setSubmitting(true);
    try {
      await http.post("/marker-collection/public/name-winner", form);
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally { setSubmitting(false); }
  };

  // ─── SUCCESS STATE ───
  if (submitted) return (
    <div className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden px-4" style={{ background: C.bg }}>
      <Confetti />
      <div className="text-center z-10 max-w-lg mx-auto" data-testid="name-winner-success">
        <div className="mb-6">
          <CheckCircle size={64} className="mx-auto mb-4 animate-bounce" style={{ color: "#4ade80" }} />
        </div>
        <h1 className="text-5xl sm:text-6xl font-black tracking-tight mb-4"
          style={{ fontFamily: F.cond, color: C.gold, textShadow: `0 0 40px ${C.gold}40` }}>
          You're All Set!
        </h1>
        <p className="text-lg leading-relaxed mb-8" style={{ fontFamily: F.sans, color: C.text2 }}>
          We've got your info. Mickey will reach out soon with your print. Keep being awesome!
        </p>
        <a href="/marker-collection" className="inline-block px-8 py-3 text-[11px] tracking-[0.2em] uppercase font-bold border transition-colors hover:bg-[#C9A055]/10"
          style={{ fontFamily: F.sans, color: C.gold, borderColor: `${C.gold}40` }}>
          Browse The Collection
        </a>
      </div>
    </div>
  );

  // ─── FORM STATE ───
  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: C.bg }}>
      <Confetti />

      {/* Hero */}
      <div className="relative z-10 text-center pt-16 sm:pt-24 pb-8 px-4">
        <div className="inline-block mb-6 px-5 py-2" style={{ background: `${C.gold}15`, border: `1px solid ${C.gold}30` }}>
          <span className="text-[10px] tracking-[0.35em] uppercase font-bold" style={{ fontFamily: F.sans, color: C.gold }}>
            The Marker Collection
          </span>
        </div>

        <h1 className="text-6xl sm:text-8xl font-black tracking-tight leading-[0.9] mb-4"
          style={{ fontFamily: F.cond, color: C.text }}
          data-testid="name-winner-heading">
          <SparkleText>CONGRATS!</SparkleText>
        </h1>

        <p className="text-xl sm:text-2xl font-bold tracking-tight mb-2"
          style={{ fontFamily: F.cond, color: C.gold }}>
          Your Name Was Chosen!
        </p>

        <p className="text-base max-w-md mx-auto leading-relaxed" style={{ fontFamily: F.sans, color: C.text2 }}>
          Mickey picked your name for one of his pieces. Fill out the form below so we can get your print to you.
        </p>
      </div>

      {/* Form Card */}
      <div className="relative z-10 max-w-lg mx-auto px-4 pb-16">
        <form onSubmit={handleSubmit}
          className="border overflow-hidden"
          style={{ background: C.bg2, borderColor: `${C.gold}20` }}
          data-testid="name-winner-form">

          {/* Form Header */}
          <div className="px-6 py-4 border-b" style={{ borderColor: `${C.gold}15`, background: `${C.gold}08` }}>
            <span className="text-[10px] tracking-[0.3em] uppercase font-bold" style={{ fontFamily: F.sans, color: C.gold }}>
              Claim Your Print
            </span>
          </div>

          <div className="p-6 space-y-5">
            {/* Name */}
            <div>
              <label className="text-[9px] tracking-[0.25em] uppercase block mb-1.5 flex items-center gap-1.5"
                style={{ fontFamily: F.sans, color: C.text2 }}>
                Your Name *
              </label>
              <input className="stc-input w-full text-sm" value={form.name} onChange={e => set("name", e.target.value)}
                placeholder="Full name" required data-testid="name-winner-name" />
            </div>

            {/* Artwork they named */}
            <div>
              <label className="text-[9px] tracking-[0.25em] uppercase block mb-1.5 flex items-center gap-1.5"
                style={{ fontFamily: F.sans, color: C.text2 }}>
                <Image size={12} /> The Name You Chose for the Artwork *
              </label>
              <input className="stc-input w-full text-sm" value={form.artwork_name} onChange={e => set("artwork_name", e.target.value)}
                placeholder="e.g. 'Whispers of the Smokies'" required data-testid="name-winner-artwork" />
            </div>

            {/* Phone */}
            <div>
              <label className="text-[9px] tracking-[0.25em] uppercase block mb-1.5 flex items-center gap-1.5"
                style={{ fontFamily: F.sans, color: C.text2 }}>
                <Phone size={12} /> Phone Number
              </label>
              <input className="stc-input w-full text-sm" value={form.phone} onChange={e => set("phone", e.target.value)}
                placeholder="(555) 555-5555" data-testid="name-winner-phone" />
            </div>

            {/* Address */}
            <div>
              <label className="text-[9px] tracking-[0.25em] uppercase block mb-1.5 flex items-center gap-1.5"
                style={{ fontFamily: F.sans, color: C.text2 }}>
                <MapPin size={12} /> Mailing Address
              </label>
              <textarea className="stc-input w-full text-sm min-h-[80px]" value={form.address} onChange={e => set("address", e.target.value)}
                placeholder="123 Main St, Kingsport, TN 37660" data-testid="name-winner-address" />
              <p className="text-[9px] mt-1" style={{ fontFamily: F.sans, color: `${C.text2}80` }}>
                For shipping or to arrange a meet-up for hand delivery
              </p>
            </div>

            {/* Message */}
            <div>
              <label className="text-[9px] tracking-[0.25em] uppercase block mb-1.5 flex items-center gap-1.5"
                style={{ fontFamily: F.sans, color: C.text2 }}>
                <MessageSquare size={12} /> Message (optional)
              </label>
              <textarea className="stc-input w-full text-sm min-h-[70px]" value={form.message} onChange={e => set("message", e.target.value)}
                placeholder="Anything you'd like Mickey to know..." data-testid="name-winner-message" />
            </div>
          </div>

          {/* Submit */}
          <div className="px-6 py-5 border-t" style={{ borderColor: `${C.gold}15`, background: `${C.gold}05` }}>
            <button type="submit" disabled={submitting}
              className="w-full py-4 text-[11px] tracking-[0.25em] uppercase font-bold flex items-center justify-center gap-2 transition-all disabled:opacity-50 hover:brightness-110"
              style={{ fontFamily: F.sans, background: C.gold, color: "#000" }}
              data-testid="name-winner-submit">
              {submitting ? (
                <div className="w-5 h-5 border-2 border-black/30 border-t-black animate-spin rounded-full" />
              ) : (
                <><Send size={14} /> Send My Info</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
